import React, { useState, useMemo } from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { PageLoader } from '../components/ui/Spinner';
import { useApp } from '../context/AppContext';
import { fmt } from '../utils/format';
import { useNavigate } from 'react-router-dom';
import './Billing.css';

/**
 * Billing page — POST /api/invoices
 * Matches the original mockup's exact look, feel, and logic:
 *  - rate-row is only visible when the card is checked (isSel is true)
 *  - Warning box appears when rates are missing
 *  - Matches the table columns and classes of inv-preview
 */
export default function Billing() {
  const { state, dispatch, doGenerateInvoice, cl } = useApp();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);

  const billable = state.workcards.filter(x => x.status === 'completed');
  const sel      = state.bilSel;

  const selCards = useMemo(() => billable.filter(x => sel.includes(x.id)), [billable, sel]);

  const clientIds    = [...new Set(selCards.map(w => w.client_id).filter(Boolean))];
  const singleClient = clientIds.length === 1;
  const client_id    = singleClient ? clientIds[0] : null;
  const hasUnlinked  = selCards.some(w => !w.client_id);

  // Preview calculations
  const lines = useMemo(() => {
    return selCards.map(w => {
      const rateStr = state.bilRates[w.id] || '';
      const rateNum = parseFloat(rateStr) || 0;
      return {
        w,
        rate: rateStr,
        rateNum,
        amount: rateNum > 0 ? rateNum * (w.qty || 0) : 0
      };
    });
  }, [selCards, state.bilRates]);

  const subtotal = useMemo(() => lines.reduce((sum, line) => sum + line.amount, 0), [lines]);
  const cgst     = subtotal * 0.09;
  const sgst     = subtotal * 0.09;
  const total    = subtotal + cgst + sgst;

  const allRatesValid = useMemo(() => {
    return sel.length > 0 && lines.every(line => line.rateNum > 0);
  }, [sel.length, lines]);

  const canGenerate = sel.length > 0 && allRatesValid && singleClient && !hasUnlinked;

  async function handleGenerate() {
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const line_items = lines.map(line => ({
        work_card_id: line.w.id,
        rate_per_sheet: line.rateNum
      }));
      await doGenerateInvoice({ client_id, line_items });
      navigate('/invoices');
    } catch (_) {}
    finally { setGenerating(false); }
  }

  if (state.loading.workcards && !state.workcards.length) {
    return <Layout><PageLoader label="Loading work cards…" /></Layout>;
  }

  if (!billable.length) {
    return (
      <Layout>
        <div className="glass-card billing-empty-container">
          <div className="empty">
            <div className="empty-ico">
              <i className="fa-solid fa-file-invoice-dollar"></i>
            </div>
            <h2 className="empty-title">No Completed Tickets Awaiting Billing</h2>
            <p className="empty-desc">
              When production completes work on a ticket, it will appear here automatically, ready to be compiled into a client invoice.
            </p>
            <Button variant="gold" onClick={() => navigate('/workcards')}>
              Go to Work Tickets <i className="fa-solid fa-circle-arrow-right" style={{ marginLeft: 6 }}></i>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Invoice Number mock (mirrors mockup: ASW-INV-2026-001 format)
  const invNo = `ASW-INV-2026-${String(state.invoices.length + 1).padStart(3, '0')}`;

  return (
    <Layout>
      <div className="billing-grid">
        {/* ── Left Column: Select Work Cards ── */}
        <div>
          <div className="card">
            <div className="card-head">
              <div>
                <div className="card-title">Select Work Cards</div>
                <div className="card-sub">Completed, unbilled cards only</div>
              </div>
            </div>
            <div style={{ padding: 16 }}>
              {billable.map(w => {
                const isSel   = sel.includes(w.id);
                const client  = cl(w.client_id);
                const rateVal = state.bilRates[w.id] || '';
                const lineAmt = rateVal > 0 ? parseFloat(rateVal) * (w.qty || 0) : 0;

                return (
                  <div key={w.id} className={`cb-item ${isSel ? 'sel' : ''}`}>
                    <div className="cb-row">
                      <input
                        type="checkbox"
                        id={`cb_${w.id}`}
                        checked={isSel}
                        onChange={() => dispatch({ type: 'TOGGLE_BIL_SEL', payload: { id: w.id } })}
                      />
                      <label htmlFor={`cb_${w.id}`} style={{ flex: 1 }}>
                        <div className="cb-name">#{w.no} — {client?.name || <span style={{ color: 'var(--rose)' }}>Unlinked</span>}</div>
                        <div className="cb-detail">
                          {w.size} · {w.gsm} GSM · {w.qty?.toLocaleString()} Sheets · {w.job.colors || 0}C
                        </div>
                      </label>
                    </div>

                    {/* Inline Rate/Sheet input for selected card - only visible when checked */}
                    {isSel && (
                      <div className="rate-row">
                        <span className="rate-prefix">₹ / Sheet</span>
                        <input
                          className="rate-input"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Rate per sheet"
                          value={rateVal}
                          onChange={e => dispatch({ type: 'SET_BIL_RATE', payload: { id: w.id, val: e.target.value } })}
                        />
                        <span className="rate-amt" style={{ color: lineAmt > 0 ? 'var(--gold)' : 'var(--dim)' }}>
                          {lineAmt > 0 ? fmt(lineAmt) : '—'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right Column: Invoice Preview ── */}
        <div>
          <div className="card">
            <div className="card-head"><div className="card-title">Invoice Preview</div></div>
            <div style={{ padding: 16 }}>
              {sel.length === 0 ? (
                <div className="empty" style={{ padding: '32px 16px' }}>
                  <div className="empty-ico">☑️</div>
                  <div className="empty-title">Select work cards</div>
                  <div className="empty-desc">Check work cards on the left, enter rate per sheet.</div>
                </div>
              ) : (
                <>
                  <div className="inv-preview">
                    <div className="inv-from-block">
                      <div>
                        <div className="inv-company">Aishwarya Printers</div>
                        <div className="inv-addr">Sivakasi, Tamil Nadu</div>
                        <div className="inv-addr" style={{ marginTop: 6 }}>
                          To: {client_id ? cl(client_id)?.name : '—'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="inv-num">{invNo}</div>
                        <div className="inv-date">{new Date().toLocaleDateString('en-IN')}</div>
                      </div>
                    </div>

                    <table className="inv-tbl">
                      <thead>
                        <tr><th>Work Card</th><th>Description</th><th>Qty</th><th>Rate/Sheet</th><th>Amount</th></tr>
                      </thead>
                      <tbody>
                        {lines.map((line, i) => (
                          <tr key={i}>
                            <td>#{line.w.no}</td>
                            <td>{line.w.size} · {line.w.gsm} GSM · {line.w.job.colors || 0}C</td>
                            <td>{line.w.qty?.toLocaleString()}</td>
                            <td style={{ color: line.rateNum > 0 ? 'var(--head)' : 'var(--dim)' }}>
                              {line.rateNum > 0 ? '₹' + line.rate : '—'}
                            </td>
                            <td><strong>{line.amount > 0 ? fmt(line.amount) : '—'}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="inv-totals">
                      <div className="inv-tot-row"><span>Subtotal</span><span>{subtotal > 0 ? fmt(subtotal) : '—'}</span></div>
                      <div className="inv-tot-row"><span>CGST (9%)</span><span>{cgst > 0 ? fmt(cgst) : '—'}</span></div>
                      <div className="inv-tot-row"><span>SGST (9%)</span><span>{sgst > 0 ? fmt(sgst) : '—'}</span></div>
                      <div className="inv-tot-row grand">
                        <span>Total Due</span>
                        <span>{total > 0 ? fmt(total) : '—'}</span>
                      </div>
                    </div>
                  </div>

                  {hasUnlinked && (
                    <div className="infobox warn" style={{ marginTop: 12 }}>
                      ⚠️ Some selected work cards have no client linked — go to Pending Queue to link them.
                    </div>
                  )}
                  {!singleClient && !hasUnlinked && (
                    <div className="infobox warn" style={{ marginTop: 12 }}>
                      ⚠️ All selected work cards must belong to the same client.
                    </div>
                  )}
                  {!allRatesValid && (
                    <div className="infobox warn" style={{ marginTop: 12 }}>
                      ⚠️ Enter rate per sheet for all selected work cards to generate invoice.
                    </div>
                  )}

                  <Button
                    variant="gold"
                    style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
                    disabled={!canGenerate || generating}
                    onClick={handleGenerate}
                  >
                    {generating ? 'Generating…' : 'Generate Invoice & Send'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
